import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  model,
  output,
  OutputEmitterRef,
  Signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, InputComponent, MbsSize, ModalService } from 'mbs-ui-kit';
import { CompaniesFacade } from 'mbs-ui-shared@facades/companies.facade';
import { Company } from 'mbs-ui-shared@models';
import { companyNameUniqueValidator } from 'mbs-ui-shared@utils/company-name.validator';
import { noop } from 'rxjs';
import { take } from 'rxjs/operators';
import { ForceValidType } from 'wizards/models/base/other-base-models/force-valid-type';
import { CompanyWizardFacade } from 'wizards/services/entity-wizards/company/company-wizard.facade';
import { CompanyCreateResult, CompanyNameStepDefaultValue, CompanyNameStepValue } from '../../models/company-wizard-models';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, FormsModule, I18NextPipe, InputComponent],
  selector: 'mbs-company-name-step',
  standalone: true,
  templateUrl: './company-name-step.component.html'
})
export class CompanyNameStepComponent implements AfterViewInit {
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly modalService = inject(ModalService);
  private readonly companyWizardFacade = inject(CompanyWizardFacade);
  private readonly companiesFacade = inject(CompaniesFacade);

  public readonly createResults = CompanyCreateResult;

  public readonly summaryItems: string[] = ['policies', 'destinations', 'permissions', 'notifications', 'users'].map((key) =>
    this.i18nPipe.transform(`wizards:entityWizards.company.nameStep.summary.${key}`)
  );

  public readonly value = model<CompanyNameStepValue>(CompanyNameStepDefaultValue);

  public readonly nextStep: OutputEmitterRef<boolean> = output<boolean>();

  private readonly createModal = viewChild<TemplateRef<unknown>>('createModal');
  private readonly nameModel = viewChild(NgModel);

  public created: Signal<boolean> = computed(() => this.companyWizardFacade.createdCompany());
  public loading: Signal<boolean> = computed(() => this.companyWizardFacade.loading());
  public valid: Signal<boolean> = computed(() => {
    const name = this.value()?.name;

    return !!name && this.created() && name === this.companyWizardFacade.company()?.name;
  });

  ngAfterViewInit(): void {
    this.nameModel()?.control.addValidators(
      companyNameUniqueValidator(
        () => this.companiesFacade.data(),
        () => this.companyWizardFacade.company()?.name,
        () => this.i18nPipe.transform('shared:validation.existing', { control: 'name' })
      )
    );
    this.companiesFacade.loadData();
  }

  public forceValid(data: ForceValidType = null): void {
    if (this.valid()) return;

    this.validateName();

    if (!this.nameValid()) return;

    if (this.created()) return this.updateCompanyName(data?.isSave);

    this.modalService
      .openRef(this.createModal(), { size: MbsSize.md })
      .result.then((r: CompanyCreateResult) => this.createCompany(r, data?.isSave))
      .catch(noop);
  }

  public nameChangeHandler(name: string): void {
    this.value.set({ ...this.value(), name });
  }

  private nameValid(): boolean {
    const control = this.nameModel()?.control;

    return control ? control.valid : !!this.value()?.name;
  }

  private validateName(): void {
    const control = this.nameModel()?.control;

    control?.markAsDirty();
    control?.markAsTouched();
    control?.updateValueAndValidity();
  }

  private updateCompanyName(isSave = false): void {
    this.companyWizardFacade.loading.set(true);

    this.companyWizardFacade
      .updateCompanyName(this.value().name)
      .pipe(take(1))
      .subscribe({
        next: (company) => {
          this.applyCompany(company);
          this.companyWizardFacade.loading.set(false);
          this.nextStep.emit(isSave);
        },
        error: (error) => {
          this.companyWizardFacade.loading.set(false);
          this.showNameRequestError(error);
        }
      });
  }

  private applyCompany(company: Company): void {
    this.value.set({ ...this.value(), name: company.name });
  }

  private createCompany(modalResult: CompanyCreateResult, isSave = false): void {
    const name = this.value()?.name;
    const createAndContinue = modalResult === CompanyCreateResult.CreateAndContinue;

    if (!name || (!createAndContinue && modalResult !== CompanyCreateResult.CreateAndExit)) return;

    this.companyWizardFacade.loading.set(true);

    this.companyWizardFacade
      .createCompany(name)
      .pipe(take(1))
      .subscribe({
        next: (company) => {
          if (createAndContinue) {
            this.companyWizardFacade.company.set(company);
            this.companyWizardFacade.createdCompany.set(true);
            this.applyCompany(company);
            this.nextStep.emit(isSave);
          } else this.modalService.dismissAll();

          this.companyWizardFacade.loading.set(false);
        },
        error: (error) => {
          this.companyWizardFacade.loading.set(false);
          this.showNameRequestError(error);
        }
      });
  }

  private showNameRequestError(error: { error?: { detail?: string } }): void {
    const control = this.nameModel()?.control;
    const message = error?.error?.detail;

    if (!control || !message) return;

    control.markAsDirty();
    control.markAsTouched();
    control.setErrors({ nameNotUnique: { message } });
  }
}
